<template>
  <div class="obra-page">
    <div v-if="obra" class="obra-page__shell">
      <ObraVisualHero
        :project="obra"
        :author="resolvedAuthor"
        :tags="projectTags"
        :can-start-reading="canStartReading"
        :age-rating-color="ratingColor"
        :is-admin="isAdmin"
        :age-rating-ids="newAgeRatingIds"
        :age-ratings="ageRatings"
        @start-reading="startReading"
        @favorite-project="favorite(obra.id)"
        @follow-project="follow(obra.id)"
        @follow-author="gottoAuthor(resolvedAuthor.id)"
        @age-rating-picked="onAgeRatingPicked"
        @delete-project="confirmDeleteProject"
      />

      <main class="obra-page__main">
        <div class="obra-page__editorial-grid">
          <div class="obra-page__summary-stack">
            <div class="obra-page__intro-row">
              <ObraSynopsisCard
                :description="normalizedDescription"
                :short-description="shortDescription"
                :expanded="expanded"
                :can-expand="canExpandSynopsis"
                @toggle-expand="toggleExpand"
              />
              <ObraAuthorCard
                :author="resolvedAuthor"
                @open-author="openAuthor"
                @follow-author="gottoAuthor"
              />
            </div>
            <ObraStatsPanel :project="obra" :chapters="chapters" />
          </div>
        </div>

        <ObraContentTabs
          class="obra-page__tabs"
          :active-tab="activeTab"
          :chapters-count="chapters.length"
          :author-projects-count="author_projects.length"
          :fanarts-count="0"
          @change-tab="activeTab = $event"
        >
          <template v-slot:chapters>
            <ObraChapterList
              :chapters="chapters"
              :project-thumb="obra.project_thumb"
              :expanded="chaptersExpanded"
              :is-admin="isAdmin"
              :limit="chapterPreviewLimit"
              @open-chapter="openChapter"
              @like-chapter="likeTitle($event.id)"
              @delete-chapter="confirmDeleteChapter($event.id)"
              @toggle-expanded="toggleChaptersExpand"
            />
          </template>

          <template v-slot:authorProjects>
            <ObraRecommendations
              v-if="author_projects.length"
              title="Obras do Autor"
              :items="author_projects"
            />
            <div v-else class="obra-empty-state">Nenhuma outra obra do autor encontrada.</div>
          </template>

          <template v-slot:fanarts>
            <div class="obra-empty-state">Ainda nao ha fanartes para esta obra.</div>
          </template>
        </ObraContentTabs>

        <ObraRecommendations
          v-if="similar_projects.length"
          title="Obras Similares"
          :items="similar_projects"
        />
      </main>
    </div>

    <!-- Dialog de confirmação para excluir obra -->
    <v-dialog v-model="deleteProjectDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Excluir Obra</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir esta obra e todos os seus capítulos?
          Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteProjectDialog = false">Cancelar</v-btn>
          <v-btn color="error" text @click="deleteProject" :loading="deleting">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de confirmação para excluir capítulo -->
    <v-dialog v-model="deleteChapterDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Excluir Capítulo</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir este capítulo? Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteChapterDialog = false">Cancelar</v-btn>
          <v-btn color="error" text @click="deleteChapter" :loading="deleting">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Estado de carregamento -->
    <div v-if="isLoading" class="obra-page-state">
      <v-progress-circular
        indeterminate
        color="var(--zinnes-primary)"
        size="64"
      ></v-progress-circular>
      <p class="obra-page-state__loading-message">Carregando obra...</p>
    </div>

    <div v-else-if="loadError" class="obra-page-state">
      <h1>{{ loadError.title }}</h1>
      <p>{{ loadError.message }}</p>
      <v-btn class="zinnes-primary-btn" dark @click="$router.push('/')">
        Voltar para a home
      </v-btn>
    </div>

    <!-- Snackbar para notificações -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { FavoriteProjectService } from '@/server/FavoriteService';
import { FollowProjectService, FollowAuthorService } from '@/server/FollowtService';
import { getProjectDetailsById } from '@/server/homeServe';
import { LikeTitleService } from '@/server/LikeService';
import ProjectService from '@/server/ProjectService';
import TitleService from '@/server/TitleService';
import { isAdminModeActive } from '@/utils/adminMode';
import { getAgeRatings, updateProjectAgeRating } from '@/server/workService';
import { getHighestAgeRating, getProjectAgeRatingIds, normalizeAgeRatingIds } from '@/utils/ageRatings';
import { applyProjectSeo } from '@/utils/seoMeta';
import { projectSlug } from '@/utils/projectRoutes';
import ObraAuthorCard from '@/components/obra/ObraAuthorCard.vue';
import ObraChapterList from '@/components/obra/ObraChapterList.vue';
import ObraContentTabs from '@/components/obra/ObraContentTabs.vue';
import ObraRecommendations from '@/components/obra/ObraRecommendations.vue';
import ObraStatsPanel from '@/components/obra/ObraStatsPanel.vue';
import ObraSynopsisCard from '@/components/obra/ObraSynopsisCard.vue';
import ObraVisualHero from '@/components/obra/ObraVisualHero.vue';
import '@/styles/obra-page/index.css';

const CHAPTER_PREVIEW_LIMIT = 5;

export default {
  name: 'ObraOpenView',
  props: ['idObra'],
  components: {
    ObraAuthorCard,
    ObraChapterList,
    ObraContentTabs,
    ObraRecommendations,
    ObraStatsPanel,
    ObraSynopsisCard,
    ObraVisualHero,
  },
  data() {
    return {
      obra: null,
      author: null,
      chapters: [],
      similar_projects: [],
      author_projects: [],
      expanded: false,
      chaptersExpanded: false,
      isAdmin: false,
      deleteProjectDialog: false,
      deleteChapterDialog: false,
      selectedChapterId: null,
      deleting: false,
      ageRatings: [],
      newAgeRatingIds: [],
      isLoading: false,
      loadError: null,
      snackbar: {
        show: false,
        message: '',
        color: '',
        timeout: 2500,
      },
      activeTab: 'chapters',
    };
  },

  computed: {
    resolvedAuthor() {
      return this.author || this.obra?.author || {};
    },
    projectTags() {
      if (!this.obra) return [];

      return [
        this.obra.category?.name,
        this.obra.gender?.name,
        this.obra.type?.name,
      ].filter(Boolean);
    },
    canStartReading() {
      return Boolean(this.chapters.length && this.chapters[0]?.id);
    },
    canExpandSynopsis() {
      return this.normalizedDescription.length > this.synopsisPreviewLength;
    },
    synopsisPreviewLength() {
      return 180;
    },
    chapterPreviewLimit() {
      return CHAPTER_PREVIEW_LIMIT;
    },
    normalizedDescription() {
      return String(this.obra?.description || '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    shortDescription() {
      if (!this.normalizedDescription) return '';

      return this.normalizedDescription.length > this.synopsisPreviewLength
        ? `${this.normalizedDescription.slice(0, this.synopsisPreviewLength).trim()}...`
        : this.normalizedDescription;
    },
  },

  mounted() {
    this.fetchObra();
    this.checkAdminStatus();

    // Adiciona listener para mudanças no modo administrador
    window.addEventListener('adminModeChanged', this.handleAdminModeChange);
  },
  beforeDestroy() {
    // Remove listener ao destruir o componente
    window.removeEventListener('adminModeChanged', this.handleAdminModeChange);
  },
  watch: {
    idObra() {
      this.fetchObra();
    }
  },
  methods: {
    showSnackbar(message, type) {
      this.snackbar.message = message;
      this.snackbar.color = type === 'success' ? 'green' : 'red';
      this.snackbar.show = true;
    },
    toggleExpand() {
      this.expanded = !this.expanded;
    },
    toggleChaptersExpand() {
      this.chaptersExpanded = !this.chaptersExpanded;
    },
    startReading() {
      if (!this.canStartReading) return;

      this.goToReadingPage(this.chapters[0].id, this.obra?.category?.name);
    },
    openChapter(chapter) {
      if (!chapter?.id) return;

      this.goToReadingPage(chapter.id, this.obra?.category?.name);
    },
    openAuthor(authorId) {
      if (!authorId) return;

      this.$router.push(`/autor/${authorId}`);
    },

    async gottoAuthor(author_id) {
      if (!author_id) return;

      let params = {
        author_id
      }

      let service = new FollowAuthorService();
      await service.store(params);
      this.fetchObra();
    },
    async fetchObra() {
      this.isLoading = true;
      this.loadError = null;
      this.obra = null;
      this.activeTab = 'chapters';
      try {
        const data = await getProjectDetailsById(this.idObra);
        const project = data.project || {};
        const projectAgeRatingIds = getProjectAgeRatingIds(project);
        this.obra = {
          ...project,
          age_rating_ids: projectAgeRatingIds,
        };
        this.author = data.author;
        this.chapters = data.titles;
        this.similar_projects = data.similar_projects;
        this.author_projects = data.author_projects;
        this.newAgeRatingIds = projectAgeRatingIds;
        applyProjectSeo(this.obra, this.author);
        this.syncCanonicalSlug();

      } catch (error) {
        console.error('Erro ao buscar obra:', error);
        const statusCode = error.response?.status;
        this.obra = null;
        this.chapters = [];
        this.similar_projects = [];
        this.author_projects = [];
        this.loadError = statusCode === 404
          ? {
            title: 'Obra não encontrada',
            message: 'A obra que você tentou acessar não existe ou não está disponível.'
          }
          : {
            title: 'Não foi possível carregar a obra',
            message: 'Tente novamente em alguns instantes.'
          };
      } finally {
        this.isLoading = false;
      }
    },
    syncCanonicalSlug() {
      const slug = projectSlug(this.obra);
      if (!slug || this.$route.params.slug === slug) return;

      this.$router.replace({
        name: 'ObraOpenView',
        params: {
          idObra: this.obra.id,
          slug
        }
      }).catch(() => {});
    },
    goToReadingPage(idChapter, type) {
      if (type === 'Livros') {
        this.$router.push(`/obra/book/reading/${idChapter}`);
      } else {
        this.$router.push(`/${idChapter}/page`);
      }

    },
    async follow(project_id) {
      let params = {
        project_id
      }

      let service = new FollowProjectService();
      await service.store(params);
      this.fetchObra();
    },
    async likeTitle(title_id) {
      try {
        let params = {
          title_id
        }

        let service = new LikeTitleService();
        await service.store(params);
        this.fetchObra();
      } catch (error) {
        console.error('Erro ao curtir capítulo:', error);
      }
    },
    async favorite(project_id) {
      let params = {
        project_id
      }

      let service = new FavoriteProjectService();
      await service.store(params);
      this.fetchObra();
    },
    ratingColor(age) {
      if (age <= 10) return '#0095DA';
      if (age <= 12) return '#FBC115';
      if (age <= 14) return '#F58220';
      return '#EC1D25';
    },
    checkAdminStatus() {
      this.isAdmin = isAdminModeActive();

      if (this.isAdmin && !this.ageRatings.length) {
        this.loadAgeRatings();
      }
    },
    async loadAgeRatings() {
      try {
        const response = await getAgeRatings();
        this.ageRatings = response?.data || response || [];
      } catch (error) {
        console.error('Erro ao buscar faixas etárias:', error);
      }
    },
    handleAdminModeChange() {
      // Atualiza o estado quando o modo administrador é alterado
      this.checkAdminStatus();
    },
    confirmDeleteProject() {
      if (!this.isAdmin) return;

      this.deleteProjectDialog = true;
    },
    async onAgeRatingPicked(ids) {
      this.newAgeRatingIds = normalizeAgeRatingIds(ids);
      await this.saveAgeRating();
    },
    async saveAgeRating() {
      try {
        const response = await updateProjectAgeRating({
          project_id: this.obra.id,
          age_rating_ids: this.newAgeRatingIds,
        });
        const updatedProject = response?.project || response;
        const storedIds = updatedProject && (
          Object.prototype.hasOwnProperty.call(updatedProject, 'age_rating_ids')
          || Object.prototype.hasOwnProperty.call(updatedProject, 'age_ratings')
        )
          ? getProjectAgeRatingIds(updatedProject)
          : [...this.newAgeRatingIds];
        const selectedIdSet = new Set(storedIds);
        const selectedRatings = Array.isArray(updatedProject?.age_ratings)
          ? updatedProject.age_ratings
          : this.ageRatings.filter(rating => (
            selectedIdSet.has(normalizeAgeRatingIds(rating.id)[0])
          ));
        const highestRating = updatedProject?.age_rating
          || getHighestAgeRating(selectedRatings, storedIds);

        this.newAgeRatingIds = storedIds;
        this.obra = {
          ...this.obra,
          age_rating_ids: storedIds,
          age_ratings: selectedRatings,
          age_rating_id: highestRating?.id || null,
          age_rating: highestRating || null,
        };
        this.showSnackbar('Faixa etária atualizada!', 'success');
      } catch {
        this.newAgeRatingIds = getProjectAgeRatingIds(this.obra);
        this.showSnackbar('Erro ao atualizar faixa etária.', 'error');
      }
    },
    async deleteProject() {
      if (!this.isAdmin) return;

      this.deleting = true;
      try {
        const projectService = new ProjectService();
        await projectService.delete({ id: this.obra.id });

        this.deleteProjectDialog = false;
        this.showSnackbar('Obra excluída com sucesso', 'success');

        // Aguarda um pouco para garantir que o snackbar seja exibido antes de redirecionar
        setTimeout(() => {
          this.$router.push('/');
        }, 500);
      } catch (error) {
        console.error('Erro ao excluir obra:', error);
        const errorMessage = error.response?.data?.message || 'Erro ao excluir obra';
        const statusCode = error.response?.status;

        // Se a obra não foi encontrada (404), redireciona para home
        if (statusCode === 404) {
          this.showSnackbar('Obra não encontrada', 'error');
          this.deleteProjectDialog = false;

          setTimeout(() => {
            this.$router.push('/');
          }, 500);
        } else {
          this.showSnackbar(errorMessage, 'error');
          this.deleteProjectDialog = false;
        }
      } finally {
        this.deleting = false;
      }
    },
    confirmDeleteChapter(chapterId) {
      this.selectedChapterId = chapterId;
      this.deleteChapterDialog = true;
    },
    async deleteChapter() {
      this.deleting = true;
      try {
        const titleService = new TitleService();
        await titleService.delete({ id: this.selectedChapterId });

        // Remove o capítulo da lista localmente
        this.chapters = this.chapters.filter(chapter => chapter.id !== this.selectedChapterId);

        this.deleteChapterDialog = false;
        this.selectedChapterId = null;
        this.showSnackbar('Capítulo excluído com sucesso', 'success');
      } catch (error) {
        console.error('Erro ao excluir capítulo:', error);
        const errorMessage = error.response?.data?.message || 'Erro ao excluir capítulo';
        const statusCode = error.response?.status;

        // Se o capítulo não foi encontrado (404), redireciona para home
        if (statusCode === 404) {
          this.showSnackbar('Capítulo não encontrado', 'error');
          this.deleteChapterDialog = false;
          this.selectedChapterId = null;

          setTimeout(() => {
            this.$router.push('/');
          }, 500);
        } else {
          this.showSnackbar(errorMessage, 'error');
          this.deleteChapterDialog = false;
          this.selectedChapterId = null;
        }
      } finally {
        this.deleting = false;
      }
    },
  }
};
</script>
